/**
 * The inline key of the chart element: one entry per drawn series or role,
 * coloured exactly as the marks are. Computing it resolves every series
 * colour, so a species code the palette refuses throws here, before any leaf
 * renders.
 */
import type { FmLegendEntry } from '../../../types/fm-legend';
import type { FmChartSpec } from './chartTypes';
import { donutShare } from './chartData';
import {
  CASHFLOW_COLORS,
  CORRIDOR_ROW_COLORS,
  REFERENCE_DEVIATION_COLORS,
} from './chartRoleColors';
import {
  CHART_CUT_COLOR,
  CHART_KEPT_COLOR,
  paletteSeries,
  paletteSeriesPattern,
  type FmSeriesPattern,
  paletteSpecies,
} from './palette';
import { resolveSeriesColor } from './seriesColor';

/** Legends of charts whose series carry their own colour descriptors. */
function seriesLegend(chart: FmChartSpec): FmLegendEntry[] | null {
  switch (chart.kind) {
    case 'bar':
      return chart.series.map((entry) => ({
        color: resolveSeriesColor(entry.color),
        label: entry.label,
        role: entry.role,
        testId: `chart-legend-${entry.dataKey}`,
      }));
    case 'growth':
      return chart.series.map((entry) => ({
        color: resolveSeriesColor(entry.color),
        label: entry.label,
        testId: `chart-legend-${entry.key}`,
      }));
    case 'time-series':
      if (chart.legend === 'none') return [];
      return chart.series.map((entry, index) => ({
        color: resolveSeriesColor(entry.color),
        label: entry.label,
        role: entry.variant === 'secondary' ? 'reference' : undefined,
        pattern: timeSeriesPattern(chart, index),
        testId: `chart-legend-${entry.dataKey}`,
      }));
    case 'donut': {
      const visible = chart.slices.filter((slice) => slice.value > 0);
      const total = visible.reduce((sum, slice) => sum + slice.value, 0);
      return visible.map((slice) => ({
        color: resolveSeriesColor(slice.color),
        label: slice.label,
        value: donutShare(slice.value, total),
        testId: `chart-legend-${slice.id}`,
      }));
    }
    case 'scatter':
      return chart.groups.map((group) => ({
        color: resolveSeriesColor(group.color),
        label: group.label,
        testId: `chart-legend-${group.id}`,
      }));
    default:
      return null;
  }
}

function histogramLegend(chart: Extract<FmChartSpec, { kind: 'histogram' }>): FmLegendEntry[] {
  const entries: FmLegendEntry[] =
    chart.keptLabel === undefined
      ? chart.distributions.map((entry) => ({
          color: paletteSpecies(entry.speciesCode),
          label: entry.label,
          testId: `chart-legend-species-${entry.speciesCode}`,
        }))
      : [{ color: CHART_KEPT_COLOR, label: chart.keptLabel, testId: 'chart-legend-kept' }];
  if (chart.removedDistributions !== undefined) {
    entries.push({
      color: CHART_CUT_COLOR,
      label: chart.cutLabel,
      testId: 'chart-legend-cut',
    });
  }
  if (chart.modelLine) {
    entries.push({
      color: paletteSeries(0),
      label: chart.modelLine.label,
      testId: 'chart-legend-model',
    });
  }
  return entries;
}

function cashflowLegend(chart: Extract<FmChartSpec, { kind: 'cashflow' }>): FmLegendEntry[] {
  if (chart.render === 'signed-net-bars') {
    return [
      { color: CASHFLOW_COLORS.netPos, label: chart.seriesLabels.income },
      { color: CASHFLOW_COLORS.netNeg, label: chart.seriesLabels.cost },
    ];
  }
  return [
    { color: CASHFLOW_COLORS.income, label: chart.seriesLabels.income },
    { color: CASHFLOW_COLORS.cost, label: chart.seriesLabels.cost },
    { color: CASHFLOW_COLORS.net, label: chart.seriesLabels.net },
  ];
}

/** Observed, reference, and only the deviation directions the data draws. */
function referenceDeviationLegend(
  chart: Extract<FmChartSpec, { kind: 'reference-deviation' }>,
): FmLegendEntry[] {
  const deviations = chart.points.flatMap((entry) =>
    entry.observed !== null &&
    entry.reference !== null &&
    Number.isFinite(entry.observed) &&
    Number.isFinite(entry.reference)
      ? [entry.observed - entry.reference]
      : [],
  );
  const entries: FmLegendEntry[] = [
    {
      color: REFERENCE_DEVIATION_COLORS.observed,
      label: chart.observedLabel,
      testId: 'chart-legend-observed',
    },
    {
      color: REFERENCE_DEVIATION_COLORS.reference,
      label: chart.referenceLabel,
      role: 'reference',
      pattern: 'dashed',
      testId: 'chart-legend-reference',
    },
  ];
  if (deviations.some((value) => value >= 0)) {
    entries.push({
      color: REFERENCE_DEVIATION_COLORS.surplus,
      label: chart.surplusLabel,
      testId: 'chart-legend-surplus',
    });
  }
  if (deviations.some((value) => value < 0)) {
    entries.push({
      color: REFERENCE_DEVIATION_COLORS.deficit,
      label: chart.deficitLabel,
      testId: 'chart-legend-deficit',
    });
  }
  return entries;
}

/** Legends of charts whose marks are coloured by a fixed role. */
function roleLegend(chart: FmChartSpec): FmLegendEntry[] {
  switch (chart.kind) {
    case 'histogram':
      return histogramLegend(chart);
    case 'cashflow':
      return cashflowLegend(chart);
    case 'corridor-rows':
      // Only marks some row draws get a key entry.
      return [
        chart.rows.some((row) => row.bandLo !== null && row.bandHi !== null)
          ? { color: CORRIDOR_ROW_COLORS.band, label: chart.bandLabel }
          : null,
        chart.rows.some((row) => row.legalMin !== null)
          ? { color: CORRIDOR_ROW_COLORS.floor, label: chart.floorLabel }
          : null,
        chart.rows.some((row) => row.residual !== null)
          ? { color: CORRIDOR_ROW_COLORS.residual, label: chart.residualLabel }
          : null,
      ].filter((entry): entry is FmLegendEntry => entry !== null);
    case 'reference-deviation':
      return referenceDeviationLegend(chart);
    default:
      return [];
  }
}

/**
 * Computing the legend resolves every series colour, so a species code the
 * palette refuses throws here, before any leaf renders.
 */
export function chartLegend(chart: FmChartSpec): FmLegendEntry[] {
  const entries = seriesLegend(chart);
  if (entries === null) return roleLegend(chart);
  // A one-series bar or growth chart drops its key: the title and value axis
  // already name the only series, so the key would just repeat them.
  return entries.length === 1 && (chart.kind === 'bar' || chart.kind === 'growth') ? [] : entries;
}

/**
 * Line pattern of a time-series line. A single line stays solid; with two or
 * more each line takes its own pattern, so lines of close hue stay apart.
 */
export function timeSeriesPattern(
  chart: Extract<FmChartSpec, { kind: 'time-series' }>,
  index: number,
): FmSeriesPattern {
  return chart.series.length > 1 ? paletteSeriesPattern(index) : 'solid';
}
